import type { CaseRecord } from "../types";

// One plain-language explanation of the provenance marks, reused by the
// mark tooltips, the Guide overlay, the export legend, and (later) the
// legal checklist's AI disclaimer. Keep it short and honest.
export const PROVENANCE_LEGEND =
  "Quotes and dollar amounts are mechanically verified — each must match your source document exactly before it is shown. " +
  "Titles, summaries, party attributions, and category groupings are AI assistance to help you scan. " +
  "When anything matters, the verbatim quote is the authority.";

// Inline source-provenance helpers. AI output carries [rec:RECORD_ID]
// citations (see workers GROUNDING_RULES). The frontend renders each as a
// numbered superscript badge and flags factual sentences that lack any
// citation. Pure string/lookup logic — the React rendering lives in
// components/ui/ProvenanceText.tsx.

export const REC_CITATION_RE = /\[rec:([^\]\s]+)\]/g;
const ISO_DATE_RE = /\b\d{4}-\d{2}-\d{2}\b/;
// Mirrors the worker's amount detector closely enough for "is this a
// factual claim" — currency-marked or clearly-formatted figures.
const AMOUNT_RE =
  /(?:(?:USD|CAD|AUD|GBP|EUR|NZD)\s*)?[$£€]\s?\d[\d,]*(?:\.\d{1,2})?|\b\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?\b|\b\d+\.\d{2}\b/;

// Distinct record IDs cited in the text, in order of first appearance —
// this fixes the ①②③ numbering so a record keeps its number wherever it
// is cited.
export function citedRecordIds(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of text.matchAll(REC_CITATION_RE)) {
    const id = m[1]!;
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

// Map of record id → 1-based citation number (① = 1, …).
export function buildCitationNumbering(text: string): Map<string, number> {
  const map = new Map<string, number>();
  citedRecordIds(text).forEach((id, i) => map.set(id, i + 1));
  return map;
}

// Remove inline [rec:ID] tokens for clean reading in the normal view
// (citations only surface, as subtle footnotes, in "View sources").
// Tidies the whitespace/punctuation the removed token leaves behind.
export function stripCitations(text: string): string {
  return text
    .replace(REC_CITATION_RE, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
}

// Split prose into sentences for uncited-claim detection. Coarse but
// good enough — keeps trailing punctuation with each sentence.
export function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+(?=[A-Z0-9"'(])/)
    .map((s) => s)
    .filter((s) => s.trim().length > 0);
}

// A sentence asserts a hard fact (a date or monetary amount) but carries
// no [rec:...] citation — the high-risk hallucination case the red wavy
// underline flags.
export function isUncitedFactualClaim(sentence: string): boolean {
  if (sentence.includes("[rec:")) return false;
  return ISO_DATE_RE.test(sentence) || AMOUNT_RE.test(sentence);
}

export interface RecordLite {
  id: string;
  date: string;
  sender: string | null;
  subject: string | null;
  // Deterministic content-derived label (migration 0055) — preferred over the
  // subject in citation tooltips/panels; null when the record has no facts.
  label: string | null;
  excerpt: string;
}

// Index records by id with the fields the badge tooltip + right panel show.
export function indexRecords(records: CaseRecord[]): Map<string, RecordLite> {
  const map = new Map<string, RecordLite>();
  for (const r of records) {
    const body = (r.body_text ?? r.ocr_text ?? "").trim();
    map.set(r.id, {
      id: r.id,
      date: (r.record_date ?? "").slice(0, 10),
      sender: r.from_addr ?? r.to_addr ?? null,
      subject: r.subject,
      label: r.content_label?.trim() || null,
      excerpt: body.length > 400 ? `${body.slice(0, 400)}…` : body,
    });
  }
  return map;
}

// True when the text carries any citations or any uncited factual claim —
// i.e. the provenance UI has something to show.
export function hasProvenance(text: string | null | undefined): boolean {
  if (!text) return false;
  if (REC_CITATION_RE.test(text)) {
    REC_CITATION_RE.lastIndex = 0;
    return true;
  }
  return splitSentences(text).some(isUncitedFactualClaim);
}
